import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { useKnowledgeBase } from 'features/profile/hooks/use-knowledge-base';
import { useGetAccount, useUpdateAccount } from 'features/profile/hooks/use-account';
import { PersonalCard } from './personal-card';
import { toAccountUpdate } from './account-update-payload';
import { BuddyTalkCard } from './buddy-talk-card';
import { MemoryCard } from './memory-card';

/*
 * Static Preview card (sample chat, no API) parked until it can reflect real settings.
 *
 * function PreviewCard() {
 *   ...
 * }
 */

/** Personalization tab (Figma 10596:4319) on the knowledge base API from the Settings dialog. */
export function AccountPersonalizationTab() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { data: knowledgeBase, isLoading, isError } = useKnowledgeBase();
  const { data: account } = useGetAccount({ enabled: true });
  const { mutate: updateAccount, isPending: isSavingAccount } = useUpdateAccount();
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';

  if (isLoading) {
    return (
      <div data-testid="account-personalization-loading" className="flex flex-col gap-4">
        <Skeleton className="h-96 w-full rounded-section" />
        <Skeleton className="h-48 w-full rounded-section" />
      </div>
    );
  }

  if (isError) {
    return <p className={cn('py-6 text-sm', mutedColor)}>{t('SOMETHING_WENT_WRONG')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {account ? (
        <PersonalCard
          salutation={account.salutation ?? ''}
          isSaving={isSavingAccount}
          onSave={({ salutation }) => updateAccount(toAccountUpdate(account, { salutation }))}
        />
      ) : null}
      <BuddyTalkCard knowledgeBase={knowledgeBase ?? null} />
      <MemoryCard knowledgeBase={knowledgeBase ?? null} />
      {/* <PreviewCard /> — no API yet */}
    </div>
  );
}
